import Component from '@glimmer/component';
import { service } from '@ember/service';
import t from 'ember-intl/helpers/t';
import type { IntlService } from 'ember-intl';
import { Section } from 'tio-ui/components/layout';
import AlertBanner from 'tio-ui/components/alert-banner';
import Table from 'tio-ui/components/table';
import Text from 'tio-ui/components/text';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';
import {
  isCurrentPlanRow,
  parseResultSections,
  type Sf0726ResultSection,
  type SortedPlanRow,
} from 'tio-employee/utils/strategy-finder-0726';
import StrategyFinder0726PlanCopy from 'tio-employee/components/strategy-finder-0726/plan-copy';

interface PlanComparisonSignature {
  Args: {
    recommendation: RecommendationModel;
    // Optional section header override (e.g. "Previous Results" on the dashboard).
    title?: string;
    // Optional pre-parsed/filtered sections. When provided they are used as-is
    // instead of parsing the recommendation, so callers can restrict the "best"
    // and "current" columns to a subset of plans (e.g. the IDR dashboard shows
    // only income-driven plans). Defaults to parsing the recommendation.
    sections?: Sf0726ResultSection[];
  };
  Element: HTMLElement;
}

type Summary = {
  firstMonthly: number;
  highestMonthly: number;
  totalPaid: number;
  forgiveness: number;
  tlfForgiveness: number;
  pslfForgiveness: number;
  numPayments: number;
};

// One loan type's plan in a column, with the forgiveness actually applied so the
// name can annotate "+ PSLF" / "with $X TLF" (and the nothing-left-to-forgive case).
type PlanEntry = {
  loanType: string;
  plan: string | null;
  tlfAmount: number;
  pslf: boolean;
  pslfForgiveness: number;
};

// A plan-details disclosure row beneath the comparison table: the loan-type
// label (shown only when there are multiple loan types), plus the plan it maps
// to and the forgiveness flags that drive the PSLF/TLF copy blocks.
type PlanDetailRow = {
  typeLabel: string;
  plan: string;
  pslf: boolean;
  pslfForgiveness: number;
  tlfAmount: number;
  planLabel: string;
};

export default class StrategyFinder0726PlanComparison extends Component<PlanComparisonSignature> {
  @service declare intl: IntlService;

  get sections() {
    return (
      this.args.sections ??
      parseResultSections(
        this.args.recommendation.calculationInput,
        this.args.recommendation.calculationOutput
      )
    );
  }

  get headerText() {
    return this.args.title ?? this.intl.t('strategy_finder_0726.comparison.title');
  }

  // How the best plan was chosen, shown in parentheses on the best-plan column.
  get sortByLabel() {
    return this.sections[0]?.sortBy === 'total_then_first_month'
      ? this.intl.t('strategy_finder_0726.comparison.sort_total')
      : this.intl.t('strategy_finder_0726.comparison.sort_first_month');
  }

  dollars = (cents: number) => cents / 100;

  formatDollars = (value: number) =>
    this.intl.formatNumber(value, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  loanTypeLabel = (loanType: string) => {
    const key = `strategy_finder_0726.loan_types.${loanType}`;
    return this.intl.exists(key) ? this.intl.t(key) : loanType;
  };

  planLabel = (planKey: string | null) => {
    if (!planKey) {
      return this.intl.t('strategy_finder_0726.loan_no_current_plan');
    }
    const key = `strategy_finder_0726.plans.${planKey}`;
    return this.intl.exists(key) ? this.intl.t(key) : planKey;
  };

  // Plan name plus the forgiveness type actually applied to that column's row
  // (e.g. "Pay As You Earn with $5,000 Teacher Loan Forgiveness + PSLF"), so the
  // user can see why the best and current plans differ. Matches the per-loan
  // table's annotations in loan-plans-section.
  planDisplayLabel = (entry: PlanEntry) => {
    let label = this.planLabel(entry.plan);
    if (entry.tlfAmount > 0) {
      // The amount is clear from the forgiveness row, so the comparison tags the
      // plan with the spelled-out program name (no dollar level), unlike the
      // per-loan table which spells out the level instead.
      label = `${label} ${this.intl.t('strategy_finder_0726.comparison.with_tlf')}`;
    }
    if (entry.pslf) {
      // Denote PSLF plans that forgive nothing (loan repaid before the cutoff).
      const suffix =
        entry.pslfForgiveness > 0
          ? this.intl.t('strategy_finder_0726.comparison.with_pslf')
          : this.intl.t('strategy_finder_0726.comparison.with_pslf_nothing');
      label = `${label} ${suffix}`;
    }
    return label;
  };

  // Best plan = the top sorted plan for each loan type. Numeric columns are
  // summed across loan types.
  get bestSummary(): Summary {
    let firstMonthly = 0;
    let highestMonthly = 0;
    let totalPaid = 0;
    let forgiveness = 0;
    let tlfForgiveness = 0;
    let pslfForgiveness = 0;
    let numPayments = 0;

    for (const section of this.sections) {
      const best = section.plans[0];
      if (!best) {
        continue;
      }
      firstMonthly += best.first_month_payment;
      highestMonthly += best.max_monthly_payment;
      totalPaid += best.total_payment;
      forgiveness += best.forgiveness_paid;
      tlfForgiveness += best.tlf_forgiveness;
      pslfForgiveness += best.pslf_forgiveness;
      // Number of payments is the longest payoff across loans, not a sum.
      numPayments = Math.max(numPayments, best.num_payments);
    }

    return {
      firstMonthly,
      highestMonthly,
      totalPaid,
      forgiveness,
      tlfForgiveness,
      pslfForgiveness,
      numPayments,
    };
  }

  // Current plan = the plan the borrower is on. First monthly payment uses the
  // user-entered current payment; the highest monthly payment and the rest come
  // from the matching plan row.
  get currentSummary(): Summary {
    let firstMonthly = 0;
    let highestMonthly = 0;
    let totalPaid = 0;
    let forgiveness = 0;
    let tlfForgiveness = 0;
    let pslfForgiveness = 0;
    let numPayments = 0;

    for (const section of this.sections) {
      const currentRow = section.plans.find((p) => isCurrentPlanRow(p, section));
      // First monthly payment is the borrower's actual entered payment, but the
      // highest monthly payment must come from the computed current-plan row —
      // the input only has today's payment, not how an income-driven payment
      // ramps over time. Fall back to the entered payment if no row matched.
      firstMonthly += section.enteredMonthlyPaymentCents;
      highestMonthly += currentRow?.max_monthly_payment ?? section.enteredMonthlyPaymentCents;

      totalPaid += currentRow?.total_payment ?? 0;
      forgiveness += currentRow?.forgiveness_paid ?? 0;
      tlfForgiveness += currentRow?.tlf_forgiveness ?? 0;
      pslfForgiveness += currentRow?.pslf_forgiveness ?? 0;
      // Number of payments is the longest payoff across loans, not a sum.
      numPayments = Math.max(numPayments, currentRow?.num_payments ?? 0);
    }

    return {
      firstMonthly,
      highestMonthly,
      totalPaid,
      forgiveness,
      tlfForgiveness,
      pslfForgiveness,
      numPayments,
    };
  }

  // Split the forgiveness row into TLF and PSLF rows when either is in play
  // (mirroring the per-loan table's columns); otherwise show the combined total
  // so IDR-term forgiveness still appears.
  get showTlfRow() {
    return this.bestSummary.tlfForgiveness > 0 || this.currentSummary.tlfForgiveness > 0;
  }

  get showPslfRow() {
    return this.bestSummary.pslfForgiveness > 0 || this.currentSummary.pslfForgiveness > 0;
  }

  get showCombinedForgivenessRow() {
    return !this.showTlfRow && !this.showPslfRow;
  }

  // The plan-details disclosures below the table are listed per loan type, so a
  // borrower with more than one loan type can tell which plan applies to which.
  get hasMultipleLoanTypes() {
    return this.sections.length > 1;
  }

  #planDetailRow = (
    loanType: string,
    plan: string,
    row: SortedPlanRow | undefined
  ): PlanDetailRow => {
    const entry: PlanEntry = {
      loanType,
      plan,
      tlfAmount: row?.tlf_amount ?? 0,
      pslf: row?.pslf ?? false,
      pslfForgiveness: row?.pslf_forgiveness ?? 0,
    };
    return {
      typeLabel: this.loanTypeLabel(loanType),
      plan,
      pslf: entry.pslf,
      pslfForgiveness: entry.pslfForgiveness,
      tlfAmount: entry.tlfAmount,
      planLabel: this.planDisplayLabel(entry),
    };
  };

  get bestPlanRows() {
    const rows: PlanDetailRow[] = [];
    for (const section of this.sections) {
      const best = section.plans[0];
      if (best) {
        rows.push(this.#planDetailRow(section.loanType, best.plan, best));
      }
    }
    return rows;
  }

  get currentPlanRows() {
    const rows: PlanDetailRow[] = [];
    for (const section of this.sections) {
      if (!section.currentPlan) {
        continue;
      }
      const currentRow = section.plans.find((p) => isCurrentPlanRow(p, section));
      rows.push(this.#planDetailRow(section.loanType, section.currentPlan, currentRow));
    }
    return rows;
  }

  // The borrower is already on the best plan when, for every loan type, the top
  // sorted plan is their current plan.
  get currentIsBest() {
    const sections = this.sections;
    return (
      sections.length > 0 &&
      sections.every((section) => {
        const best = section.plans[0];
        return !!best && isCurrentPlanRow(best, section);
      })
    );
  }

  <template>
    {{#unless @recommendation.hasErrorsInCalcs}}
      <Section data-test-sf-0726-comparison ...attributes>
        <:header>{{this.headerText}}</:header>
        <:body>
          {{#if this.currentIsBest}}
            <AlertBanner @intent="success" class="mb-3" data-test-sf-0726-current-is-best>
              <p class="text-sm">{{t "strategy_finder_0726.comparison.current_is_best"}}</p>
            </AlertBanner>
          {{/if}}
          <Table @isLoading={{false}}>
            <:thead as |options|>
              <options.tr>
                <options.th>{{t "strategy_finder_0726.comparison.metric"}}</options.th>
                <options.th>
                  {{t "strategy_finder_0726.comparison.best_plan"}}
                  ({{this.sortByLabel}})
                </options.th>
                <options.th>{{t "strategy_finder_0726.comparison.current_plan"}}</options.th>
              </options.tr>
            </:thead>
            <:tbody as |options|>
              <options.tr class="align-top">
                <options.td>{{t "strategy_finder_0726.comparison.plans"}}</options.td>
                <options.td data-test-sf-0726-comparison-best-names>
                  {{#each this.bestPlanRows as |row|}}
                    {{#if this.hasMultipleLoanTypes}}
                      <Text @weight="semibold" @size="sm" class="block mt-2 tio-fg-secondary">
                        {{row.typeLabel}}
                      </Text>
                    {{/if}}
                    <StrategyFinder0726PlanCopy
                      @plan={{row.plan}}
                      @pslf={{row.pslf}}
                      @pslfForgiveness={{row.pslfForgiveness}}
                      @tlfAmount={{row.tlfAmount}}
                      @planName={{row.planLabel}}
                      class="mb-3"
                    />
                  {{/each}}
                </options.td>
                <options.td data-test-sf-0726-comparison-current-names>
                  {{#each this.currentPlanRows as |row|}}
                    {{#if this.hasMultipleLoanTypes}}
                      <Text @weight="semibold" @size="sm" class="block mt-2 tio-fg-secondary">
                        {{row.typeLabel}}
                      </Text>
                    {{/if}}
                    <StrategyFinder0726PlanCopy
                      @plan={{row.plan}}
                      @pslf={{row.pslf}}
                      @pslfForgiveness={{row.pslfForgiveness}}
                      @tlfAmount={{row.tlfAmount}}
                      @planName={{row.planLabel}}
                      class="mb-3"
                    />
                  {{/each}}
                </options.td>
              </options.tr>
              <options.tr>
                <options.td>{{t
                    "strategy_finder_0726.results.columns.first_month_payment"
                  }}</options.td>
                <options.td>
                  {{safeFormatNumber
                    (this.dollars this.bestSummary.firstMonthly)
                    style="currency"
                    currency="USD"
                    maximumFractionDigits=0
                  }}
                </options.td>
                <options.td>
                  {{safeFormatNumber
                    (this.dollars this.currentSummary.firstMonthly)
                    style="currency"
                    currency="USD"
                    maximumFractionDigits=0
                  }}
                </options.td>
              </options.tr>
              <options.tr>
                <options.td>{{t
                    "strategy_finder_0726.results.columns.max_monthly_payment"
                  }}</options.td>
                <options.td>
                  {{safeFormatNumber
                    (this.dollars this.bestSummary.highestMonthly)
                    style="currency"
                    currency="USD"
                    maximumFractionDigits=0
                  }}
                </options.td>
                <options.td>
                  {{safeFormatNumber
                    (this.dollars this.currentSummary.highestMonthly)
                    style="currency"
                    currency="USD"
                    maximumFractionDigits=0
                  }}
                </options.td>
              </options.tr>
              <options.tr>
                <options.td>{{t "strategy_finder_0726.results.columns.total_payment"}}</options.td>
                <options.td>
                  {{safeFormatNumber
                    (this.dollars this.bestSummary.totalPaid)
                    style="currency"
                    currency="USD"
                    maximumFractionDigits=0
                  }}
                </options.td>
                <options.td>
                  {{safeFormatNumber
                    (this.dollars this.currentSummary.totalPaid)
                    style="currency"
                    currency="USD"
                    maximumFractionDigits=0
                  }}
                </options.td>
              </options.tr>
              {{#if this.showTlfRow}}
                <options.tr data-test-sf-0726-comparison-tlf-row>
                  <options.td>{{t "strategy_finder_0726.comparison.tlf_forgiveness"}}</options.td>
                  <options.td>
                    {{safeFormatNumber
                      (this.dollars this.bestSummary.tlfForgiveness)
                      style="currency"
                      currency="USD"
                      maximumFractionDigits=0
                    }}
                  </options.td>
                  <options.td>
                    {{safeFormatNumber
                      (this.dollars this.currentSummary.tlfForgiveness)
                      style="currency"
                      currency="USD"
                      maximumFractionDigits=0
                    }}
                  </options.td>
                </options.tr>
              {{/if}}
              {{#if this.showPslfRow}}
                <options.tr data-test-sf-0726-comparison-pslf-row>
                  <options.td>{{t "strategy_finder_0726.comparison.pslf_forgiveness"}}</options.td>
                  <options.td>
                    {{safeFormatNumber
                      (this.dollars this.bestSummary.pslfForgiveness)
                      style="currency"
                      currency="USD"
                      maximumFractionDigits=0
                    }}
                  </options.td>
                  <options.td>
                    {{safeFormatNumber
                      (this.dollars this.currentSummary.pslfForgiveness)
                      style="currency"
                      currency="USD"
                      maximumFractionDigits=0
                    }}
                  </options.td>
                </options.tr>
              {{/if}}
              {{#if this.showCombinedForgivenessRow}}
                <options.tr>
                  <options.td>{{t "strategy_finder_0726.results.columns.forgiveness"}}</options.td>
                  <options.td>
                    {{safeFormatNumber
                      (this.dollars this.bestSummary.forgiveness)
                      style="currency"
                      currency="USD"
                      maximumFractionDigits=0
                    }}
                  </options.td>
                  <options.td>
                    {{safeFormatNumber
                      (this.dollars this.currentSummary.forgiveness)
                      style="currency"
                      currency="USD"
                      maximumFractionDigits=0
                    }}
                  </options.td>
                </options.tr>
              {{/if}}
              <options.tr>
                <options.td>{{t "strategy_finder_0726.results.columns.num_payments"}}</options.td>
                <options.td>{{this.bestSummary.numPayments}}</options.td>
                <options.td>{{this.currentSummary.numPayments}}</options.td>
              </options.tr>
            </:tbody>
          </Table>
        </:body>
      </Section>
    {{/unless}}
  </template>
}
